/**
 * Heading Component Variants and Constants
 *
 * CVA variants and mapping constants for the Heading component.
 * Separated to allow React Fast Refresh to work properly.
 */

import {cva} from 'class-variance-authority';
import type {HeadingLevel, HeadingSize, HeadingWeight} from './typography.types';

// =============================================================================
// CONSTANTS
// =============================================================================

/**
 * Map heading level to default size when size="auto"
 */
export const LEVEL_TO_SIZE: Record<HeadingLevel, HeadingSize> = {
  1: 'display-1',
  2: 'display-2',
  3: 'heading-1',
  4: 'heading-2',
  5: 'heading-3',
  6: 'heading-4',
};

/**
 * Map 'as' prop to level for legacy compatibility
 */
export const AS_TO_LEVEL: Record<string, HeadingLevel> = {
  h1: 1,
  h2: 2,
  h3: 3,
  h4: 4,
  h5: 5,
  h6: 6,
};

/**
 * Map size to default weight
 */
export const SIZE_TO_WEIGHT: Partial<Record<HeadingSize, HeadingWeight>> = {
  'display-1': 'bold',
  'display-2': 'bold',
  'display-3': 'bold',
  'display': 'bold',
  'heading-1': 'semibold',
  'heading-2': 'semibold',
  'heading-3': 'semibold',
  'heading-4': 'medium',
  // Legacy sizes default to light (matching old component)
  'xs': 'normal',
  'sm': 'normal',
  'md': 'normal',
  'lg': 'normal',
  'xl': 'normal',
  '2xl': 'normal',
  '3xl': 'normal',
};

// =============================================================================
// HEADING VARIANTS (CVA)
// =============================================================================

/**
 * Heading style variants using class-variance-authority.
 *
 * Size classes map to the responsive typography utilities in index.css:
 * - text-display-1, text-display-2, text-display-3
 * - text-heading-1, text-heading-2, text-heading-3, text-heading-4
 * - Legacy Tailwind sizes for backward compatibility
 */
export const headingVariants = cva(
  // Base styles
  [
    'tracking-tight', // Letter spacing for headings
  ].join(' '),
  {
    variants: {
      size: {
        // New tokenized sizes
        'display-1': 'text-display-1', // 40px → 88px (responsive)
        'display-2': 'text-display-2', // 32px → 64px (responsive)
        'display-3': 'text-display-3', // 28px → 56px (responsive)
        'heading-1': 'text-heading-1', // 28px → 44px (responsive)
        'heading-2': 'text-heading-2', // 24px → 36px (responsive)
        'heading-3': 'text-heading-3', // 20px → 28px (responsive)
        'heading-4': 'text-heading-4', // 18px → 24px (responsive)
        // Legacy sizes for backward compatibility
        'xs': 'text-sm sm:text-base',
        'sm': 'text-base sm:text-lg',
        'md': 'text-lg sm:text-xl',
        'lg': 'text-xl sm:text-2xl',
        'xl': 'text-2xl sm:text-3xl',
        '2xl': 'text-3xl sm:text-4xl',
        '3xl': 'text-4xl sm:text-5xl',
        'display': 'text-display-2 sm:text-display-1', // Legacy display alias
        auto: '', // Will be computed from level
      },
      weight: {
        light: 'font-light',
        normal: 'font-normal',
        medium: 'font-medium',
        semibold: 'font-semibold',
        bold: 'font-bold',
      },
      variant: {
        default: 'text-foreground',
        muted: 'text-muted-foreground',
        primary: 'text-primary',
        secondary: 'text-secondary',
        accent: 'text-accent',
      },
      // Legacy color prop (maps to variant)
      color: {
        primary: 'text-[hsl(var(--text-primary))]',
        secondary: 'text-[hsl(var(--text-secondary))]',
        muted: 'text-[hsl(var(--text-muted))]',
        foreground: '', // Already set by variant
        inherit: 'text-inherit',
        white: 'text-primary-foreground',
        accent: 'text-[hsl(var(--primary-color))]',
      },
      align: {
        left: 'text-left',
        center: 'text-center',
        right: 'text-right',
      },
      tracking: {
        tighter: 'tracking-tighter',
        tight: 'tracking-tight',
        normal: 'tracking-normal',
        wide: 'tracking-wide',
      },
      truncate: {
        true: 'truncate',
        false: '',
      },
      balance: {
        true: 'text-balance',
        false: '',
      },
      // Legacy margin bottom
      mb: {
        0: 'mb-0',
        1: 'mb-1',
        2: 'mb-2',
        3: 'mb-3',
        4: 'mb-4',
        6: 'mb-6',
        8: 'mb-8',
      },
    },
    defaultVariants: {
      variant: 'default',
      align: 'left',
      tracking: 'tight',
      truncate: false,
      balance: false,
      mb: 0,
    },
  }
);

/**
 * Line clamp classes
 */
export const lineClampClasses: Record<1 | 2 | 3 | 4 | 5 | 6, string> = {
  1: 'line-clamp-1',
  2: 'line-clamp-2',
  3: 'line-clamp-3',
  4: 'line-clamp-4',
  5: 'line-clamp-5',
  6: 'line-clamp-6',
};
